/**
 * Template Method Selector
 * Allows user to choose how to generate proposal template:
 * 1. Upload RFP Document
 * 2. Import from Previous Report
 * 3. AI-Derived Template
 */

import React from 'react';
import { Card, Button } from '@tracekit/core';
import { Upload, FileText, Brain, ArrowRight } from 'lucide-react';

export type TemplateMethod = 'upload_rfp' | 'import_report' | 'ai_derived';

interface TemplateMethodSelectorProps {
  onSelectMethod: (method: TemplateMethod) => void;
  onCancel?: () => void;
}

export function TemplateMethodSelector({
  onSelectMethod,
  onCancel,
}: TemplateMethodSelectorProps) {
  return (
    <div className="space-y-6">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-theme-text mb-2">
          Choose Template Generation Method
        </h2>
        <p className="text-gray-600 dark:text-theme-text-secondary">
          Select how you'd like to generate the proposal template
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {/* Option 1: Upload RFP Document */}
        <div
          className="cursor-pointer"
          onClick={() => onSelectMethod('upload_rfp')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelectMethod('upload_rfp');
            }
          }}
        >
          <Card className="p-6 hover:shadow-lg transition-all border-2 border-transparent hover:border-blue-300 dark:hover:border-blue-700 h-full">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="p-4 bg-blue-100 dark:bg-blue-900/30 rounded-full">
                <Upload className="h-8 w-8 text-blue-600 dark:text-blue-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-theme-text">
                Upload RFP Document
              </h3>
              <p className="text-sm text-gray-600 dark:text-theme-text-secondary">
                Upload the RFP/RFSO document from the opportunity provider. The system will extract the structure and requirements to create a tailored template.
              </p>
              <div className="flex items-center text-blue-600 dark:text-blue-400 text-sm font-medium">
                Upload & Process
                <ArrowRight className="h-4 w-4 ml-1" />
              </div>
            </div>
          </Card>
        </div>

        {/* Option 2: Import from Previous Report */}
        <div
          className="cursor-pointer"
          onClick={() => onSelectMethod('import_report')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelectMethod('import_report');
            }
          }}
        >
          <Card className="p-6 hover:shadow-lg transition-all border-2 border-transparent hover:border-green-300 dark:hover:border-green-700 h-full">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="p-4 bg-green-100 dark:bg-green-900/30 rounded-full">
                <FileText className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-theme-text">
                Import from Previous Report
              </h3>
              <p className="text-sm text-gray-600 dark:text-theme-text-secondary">
                Select a previous successful proposal and reuse its template structure. Questions can be adapted to the new opportunity.
              </p>
              <div className="flex items-center text-green-600 dark:text-green-400 text-sm font-medium">
                Browse Reports
                <ArrowRight className="h-4 w-4 ml-1" />
              </div>
            </div>
          </Card>
        </div>

        {/* Option 3: AI-Derived Template */}
        <div
          className="cursor-pointer"
          onClick={() => onSelectMethod('ai_derived')}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelectMethod('ai_derived');
            }
          }}
        >
          <Card className="p-6 hover:shadow-lg transition-all border-2 border-transparent hover:border-purple-300 dark:hover:border-purple-700 h-full">
            <div className="flex flex-col items-center text-center space-y-4">
              <div className="p-4 bg-purple-100 dark:bg-purple-900/30 rounded-full">
                <Brain className="h-8 w-8 text-purple-600 dark:text-purple-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-theme-text">
                AI-Derived Template
              </h3>
              <p className="text-sm text-gray-600 dark:text-theme-text-secondary">
                Use AI to analyze the opportunity and your company's past proposals to generate a customized template structure.
              </p>
              <div className="flex items-center text-purple-600 dark:text-purple-400 text-sm font-medium">
                Generate with AI
                <ArrowRight className="h-4 w-4 ml-1" />
              </div>
            </div>
          </Card>
        </div>
      </div>

      {onCancel && (
        <div className="flex justify-center pt-4">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
